import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import Modal from "../components/Modal";
import MobileMenu from "../components/MobileMenu";
import Menu, { type MenuItem } from "../components/Menu";
import "./Occupants.css";
import { sessionService, lotPricingService, lotService } from "../utils/api";
import { useLot } from "../contexts/LotContext";
import { useUser } from "../contexts/UserContext";
import { useMultiLot } from "../contexts/MultiLotContext";
import Error from "../components/Error";
import Tooltip from "../components/Tooltip";
import { isSuperadminModeEnabled, SUPERADMIN_MODE_EVENT, SUPERADMIN_MODE_STORAGE_KEY } from "../components/SuperadminHeader";
import { SessionWithLot, PaginationParams, PageResponse } from "../types";
import { aggregateSessions, sumCapacities } from "../utils/multiLotAggregation";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";

// Threshold for condensed mode (when content area is narrow)
const CONDENSED_WIDTH_THRESHOLD = 700;

interface ParkingSession {
  sessionId: string;
  plateNumber: string;
  ownerName: string;
  lotId: string;
  entryAlprId: string;
  exitAlprId: string;
  entryTime: string;
  exitTime: string;
  departed: boolean;
  advancedPricingEnabled: boolean;
  forceEnd: boolean;
  pricingId: string;
  totalAmount: number;
  vehicleRegistered: boolean;
  sessionType: string;
  userType: string;
  vehicleType: string;
  dwellTime: number;
  dwellPeriodTimeScale: string;
  deleted: boolean;
}

type ModalType = "removeVehicle" | "removeValidation" | "clearAll" | "autoTimeRange" | "validateViolator" | null;

const Occupants: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const { lotData, isDemoLot } = useLot(); // Use the lot context to get lot data
  const { user } = useUser(); // Use the user context to get current user data

  // Multi-lot context
  const { isMultiLotMode, selectedLots, getSelectedLotIds, getSelectedLotNames } = useMultiLot();
  const selectedLotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];
  const lotNameMap = getSelectedLotNames();

  // Local sessions state (for multi-lot mode or local updates)
  const [localSessions, setLocalSessions] = useState<SessionWithLot[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lotPricing, setLotPricing] = useState<any>(null);
  const [autoForceEndThreshold, setAutoForceEndThreshold] = useState<string>("NONE");
  const [isAutoMenuOpen, setIsAutoMenuOpen] = useState(false);
  const [pendingTimeRange, setPendingTimeRange] = useState<number | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Map sort column names to API field names
  const sortColumnToApiField: Record<string, string> = {
    plate: "plateNumber",
    arrivalTime: "entryTime",
    arrivalDate: "entryTime",
    duration: "entryTime",
    status: "sessionType",
    lotName: "lotName",
  };

  // Fetch function for pagination hook
  const fetchSessionsData = useCallback(
    async (params: PaginationParams): Promise<PageResponse<SessionWithLot>> => {
      const response = await sessionService.getActiveSessionsByLotPaginated(lotId!, params);
      // Add lotName to each session for consistency
      const sessionsWithLotName = response.content.map((session: any) => ({
        ...session,
        lotName: lotData?.lotName || "Unknown Lot",
      }));
      return {
        ...response,
        content: sessionsWithLotName,
      };
    },
    [lotId, lotData?.lotName]
  );

  // Use pagination hook for single-lot mode
  const {
    items: serverSessions,
    totalElements,
    totalPages,
    page,
    sortBy,
    sortDirection,
    loading: paginationLoading,
    goToPage,
    setSort,
    setSearch,
    refresh: refreshPagination,
  } = usePagination<SessionWithLot>({
    fetchFn: fetchSessionsData,
    initialPage: 0,
    initialSize: 10,
    initialSortBy: "entryTime",
    initialSortDirection: "asc",
    enabled: !!lotId && !isMultiLotMode,
    resetKey: lotId,
  });

  // Combined sessions: use server data for single-lot, local for multi-lot
  const sessions = isMultiLotMode ? localSessions : serverSessions;
  const loading = isMultiLotMode ? isInitialLoading : paginationLoading;

  // For modal control
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [modalSessionId, setModalSessionId] = useState<string | null>(null);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuPosition, setMobileMenuPosition] = useState({ x: 0, y: 0 });
  const [mobileMenuSession, setMobileMenuSession] = useState<ParkingSession | null>(null);

  // Get lot capacity from the context (or sum across selected lots in multi-lot mode)
  const lotCapacity = isMultiLotMode ? sumCapacities(selectedLots) : (lotData?.lotCapacity ?? 0);
  const multiLotOccupancyFromLots = useMemo(() => {
    if (!isMultiLotMode) return null;
    const values = selectedLots
      .map((lot) => (typeof lot.currentOccupancy === "number" ? lot.currentOccupancy : null))
      .filter((v) => v !== null) as number[];
    if (values.length === 0) return null;
    return values.reduce((sum, occ) => sum + occ, 0);
  }, [isMultiLotMode, selectedLots]);

  // Override occupancy state
  const [currentOccupancy, setCurrentOccupancy] = useState<number | null>(lotData?.currentOccupancy ?? null);
  const [isSuperadminModeOn, setIsSuperadminModeOn] = useState<boolean>(() => isSuperadminModeEnabled());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [overrideValue, setOverrideValue] = useState<string>("");
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [isSavingOverride, setIsSavingOverride] = useState(false);

  // Condensed mode state (for narrow content areas)
  const [isCondensed, setIsCondensed] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Fetch lot pricing data
  const fetchLotPricing = async () => {
    if (!lotId) return;
    try {
      const pricingData = await lotPricingService.getLatestPricingByLotId(lotId);
      setLotPricing(pricingData);
    } catch (err) {
      console.error("Failed to fetch lot pricing:", err);
      setError("Failed to load lot pricing data. Please try again.");
    }
  };

  // Fetch lot data including auto force end threshold
  const fetchLotData = async () => {
    if (!lotId) return;
    try {
      const lotData = await lotService.getLotById(lotId);
      setAutoForceEndThreshold(lotData.autoForceEndThreshold || "NONE");
      if (typeof lotData?.currentOccupancy === "number") {
        setCurrentOccupancy(lotData.currentOccupancy);
      }
    } catch (err) {
      console.error("Failed to fetch lot data:", err);
    }
  };

  const fetchCurrentOccupancy = async () => {
    if (!lotId) return;
    try {
      const occupancy = await lotService.getCurrentOccupancy(lotId);
      if (typeof occupancy === "number") {
        setCurrentOccupancy(occupancy);
      }
    } catch {
      // Ignore occupancy fetch errors to avoid blocking the page
    }
  };

  // Fetch sessions from the backend (multi-lot mode only, single-lot uses pagination hook)
  const fetchSessions = async () => {
    if (!isMultiLotMode) {
      // Single-lot mode uses the pagination hook
      refreshPagination();
      return;
    }

    setIsInitialLoading(true);
    try {
      // Fetch sessions from all selected lots in parallel
      const responses = await Promise.all(
        selectedLotIds.map(id => sessionService.getSessionsByLot(id))
      );

      // Aggregate sessions with lot names
      const sessionsArrays = responses.map(response =>
        response.filter(
          (session: ParkingSession) => !session.departed && !session.deleted
        )
      );
      const allSessions = aggregateSessions(sessionsArrays, lotNameMap);

      setLocalSessions(allSessions);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
      setError("Failed to load sessions. Please try again.");
      setLocalSessions([]);
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // Only fetch lot-specific data in single-lot mode
    if (!isMultiLotMode) {
      fetchLotPricing();
      fetchLotData();
      fetchCurrentOccupancy();
    } else {
      // In multi-lot mode, sum occupancies from selected lots
      const fetchMultiLotOccupancy = async () => {
        try {
          const occupancies = await Promise.all(
            selectedLotIds.map(id => lotService.getCurrentOccupancy(id))
          );
          const totalOccupancy = occupancies.reduce((sum: number, occ) => sum + (occ || 0), 0);
          setCurrentOccupancy(totalOccupancy);
        } catch {
          // Ignore occupancy fetch errors
        }
      };
      fetchMultiLotOccupancy();
    }
    // Setup a refresh interval to keep data current (only in single-lot mode)
    const interval = isMultiLotMode ? null : setInterval(fetchSessions, 3000000); // refresh every 50 minutes
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lotId, isMultiLotMode, selectedLotIds.join(",")]);

  const occupantCount = sessions.length;

  useEffect(() => {
    if (typeof lotData?.currentOccupancy === "number") {
      setCurrentOccupancy(lotData.currentOccupancy);
    }
  }, [lotData]);

  // Check if validation is allowed using the API data
  const allowValidation = lotPricing?.allowValidation ?? false;

  const isSuperadmin = (user?.role || "").toLowerCase() === "superadmin";
  const canOverrideOccupancy = isSuperadmin && isSuperadminModeOn;
  const canUseSuperadminTools = isSuperadmin && isSuperadminModeOn;
  const demoBlocksAction = isDemoLot && !canUseSuperadminTools;
  const [demoBlockMessage, setDemoBlockMessage] = useState<string | null>(null);

  // Track superadmin mode changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setIsSuperadminModeOn(detail.enabled);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setIsSuperadminModeOn(event.newValue === "true");
      }
    };

    window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Track container width for condensed mode
  const checkCondensed = useCallback(() => {
    if (!tableContainerRef.current) return;
    const width = tableContainerRef.current.offsetWidth;
    setIsCondensed(width < CONDENSED_WIDTH_THRESHOLD);
  }, []);

  useEffect(() => {
    const element = tableContainerRef.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      checkCondensed();
    });

    observer.observe(element);
    checkCondensed();

    return () => observer.disconnect();
  }, [checkCondensed]);

  const handleOpenOverrideModal = () => {
    if (!canOverrideOccupancy) return;
    setOverrideError(null);
    setOverrideValue(
      currentOccupancy !== null && currentOccupancy !== undefined
        ? currentOccupancy.toString()
        : ""
    );
    setIsEditModalOpen(true);
  };

  const handleCloseOverrideModal = () => {
    setIsEditModalOpen(false);
    setOverrideError(null);
  };

  const handleOverrideInputChange = (value: string) => {
    const sanitized = value.replace(/[^\d]/g, "");
    if (sanitized === "") {
      setOverrideValue("");
      return;
    }
    const parsed = Math.min(10000, parseInt(sanitized, 10));
    setOverrideValue(parsed.toString());
  };

  const handleSaveOverride = async () => {
    if (!lotId) return;
    const parsedValue = parseInt(overrideValue, 10);
    if (Number.isNaN(parsedValue) || parsedValue < 0 || parsedValue > 10000) {
      setOverrideError("Enter a whole number between 0 and 10000.");
      return;
    }
    setIsSavingOverride(true);
    setOverrideError(null);
    try {
      const updatedValue = await lotService.overrideCurrentOccupancy(
        lotId,
        parsedValue
      );
      setCurrentOccupancy(updatedValue);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Failed to override occupancy:", error);
      setOverrideError("Failed to save occupancy. Please try again.");
    } finally {
      setIsSavingOverride(false);
    }
  };

  // Helper to set occupant validated
  const handleValidate = async (sessionId: string) => {
    if (!lotId) {
      setError("Lot ID is missing. Please try again.");
      return;
    }

    if (!user?.userId) {
      setError("User ID is missing. Please try again.");
      return;
    }

    try {
      console.log("Validating session", sessionId, "for lot", lotId);
      await sessionService.validateSession(sessionId, lotId, true, user.userId);

      // Update state to reflect change immediately
      if (isMultiLotMode) {
        setLocalSessions((prev: SessionWithLot[]) =>
          prev.map((s: SessionWithLot) =>
            s.sessionId === sessionId ? { ...s, sessionType: "Validated" } : s
          )
        );
      } else {
        refreshPagination();
      }
    } catch (err) {
      console.error("Failed to validate session:", err);
      setError("Failed to validate vehicle. Please try again.");
    }
  };

  // Handle validation for violators
  const handleValidateViolatorModal = (sessionId: string) => {
    setModalType("validateViolator");
    setModalSessionId(sessionId);
    setModalOpen(true);
  };

  const confirmValidateViolator = async () => {
    if (!modalSessionId || !lotId) {
      setError("Session ID or Lot ID is missing. Please try again.");
      return;
    }

    if (!user?.userId) {
      setError("User ID is missing. Please try again.");
      return;
    }

    try {
      console.log("Validating violator session", modalSessionId, "for lot", lotId);
      await sessionService.validateSession(modalSessionId, lotId, true, user.userId);

      // Update state to reflect change immediately
      if (isMultiLotMode) {
        setLocalSessions((prev: SessionWithLot[]) =>
          prev.map((s: SessionWithLot) =>
            s.sessionId === modalSessionId ? { ...s, sessionType: "Validated" } : s
          )
        );
      } else {
        refreshPagination();
      }
    } catch (err) {
      console.error("Failed to validate violator session:", err);
      setError("Failed to validate vehicle. Please try again.");
    }
    closeModal();
  };

  // If occupant is validated, we want a modal to remove validation
  const handleRemoveValidationModal = (sessionId: string) => {
    setModalType("removeValidation");
    setModalSessionId(sessionId);
    setModalOpen(true);
  };

  const confirmRemoveValidation = async () => {
    if (!modalSessionId || !lotId) {
      setError("Session ID or Lot ID is missing. Please try again.");
      return;
    }

    if (!user?.userId) {
      setError("User ID is missing. Please try again.");
      return;
    }

    try {
      console.log("Removing validation for session", modalSessionId, "for lot", lotId);
      await sessionService.validateSession(modalSessionId, lotId, false, user.userId);

      // Update state
      if (isMultiLotMode) {
        setLocalSessions((prev: SessionWithLot[]) =>
          prev.map((s: SessionWithLot) =>
            s.sessionId === modalSessionId ? { ...s, sessionType: "Visitor" } : s
          )
        );
      } else {
        refreshPagination();
      }
    } catch (err) {
      console.error("Failed to remove validation:", err);
      setError("Failed to remove validation. Please try again.");
    }
    closeModal();
  };

  // Show a modal before removing occupant from the lot
  const handleRemoveVehicleModal = (sessionId: string) => {
    setModalType("removeVehicle");
    setModalSessionId(sessionId);
    setModalOpen(true);
  };

  const confirmRemoveVehicle = async () => {
    if (!modalSessionId || !lotId) {
      setError("Session ID or Lot ID is missing. Please try again.");
      return;
    }

    if (!user?.userId) {
      setError("User ID is missing. Please try again.");
      return;
    }

    try {
      console.log("Force ending session", modalSessionId, "for lot", lotId);
      await sessionService.forceEndSession(modalSessionId, lotId, user.userId);
      console.log("Force end session completed");

      // Wait before refreshing (gives the backend time to process)
      await new Promise(resolve => setTimeout(resolve, 500));

      // Re-fetch sessions to update the UI
      await fetchSessions();

      console.log("Sessions after force end:", sessions);
    } catch (err: any) {
      console.error("Failed to force end session:", err);
      if (err.response) {
        console.error("Error response:", err.response.data);
        console.error("Status:", err.response.status);
      }
      setError("Failed to remove vehicle. Please try again.");
    }
    closeModal();
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
    setModalSessionId(null);
    setPendingTimeRange(null);
  };

  // Mobile menu handlers
  const handleMobileMenuOpen = (session: ParkingSession, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    setMobileMenuPosition({
      x: rect.left,
      y: rect.bottom + 5
    });
    setMobileMenuSession(session);
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileMenuSession(null);
  };

  const handleMobileValidate = () => {
    if (!mobileMenuSession) return;

    const session = mobileMenuSession;
    const isVal = session.sessionType === "Validated";

    if (isVal) {
      handleRemoveValidationModal(session.sessionId);
    } else if (session.userType === "Violator") {
      handleValidateViolatorModal(session.sessionId);
    } else {
      handleValidate(session.sessionId);
    }
  };

  const handleMobileRemove = () => {
    if (!mobileMenuSession) return;
    handleRemoveVehicleModal(mobileMenuSession.sessionId);
  };

  // Handle clear all sessions
  const handleClearAllModal = () => {
    if (demoBlocksAction) {
      setDemoBlockMessage("Clear All is not available in demo mode.");
      return;
    }
    setModalType("clearAll");
    setModalOpen(true);
  };

  const confirmClearAll = async () => {
    if (!lotId && !isMultiLotMode) {
      setError("Lot ID is missing. Please try again.");
      return;
    }

    try {
      if (isMultiLotMode) {
        // Clear all sessions for all selected lots in parallel
        console.log("Force ending all sessions for lots", selectedLotIds);
        await Promise.all(
          selectedLotIds.map(id => sessionService.forceEndAllSessions(id))
        );
        console.log("Force end all sessions completed for all lots");
      } else {
        console.log("Force ending all sessions for lot", lotId);
        await sessionService.forceEndAllSessions(lotId!);
        console.log("Force end all sessions completed");
      }

      // Wait before refreshing (gives the backend time to process)
      await new Promise(resolve => setTimeout(resolve, 500));

      // Re-fetch sessions to update the UI
      await fetchSessions();

      console.log("Sessions after force end all:", sessions);
    } catch (err: any) {
      console.error("Failed to force end all sessions:", err);
      if (err.response) {
        console.error("Error response:", err.response.data);
        console.error("Status:", err.response.status);
      }
      setError("Failed to clear all vehicles. Please try again.");
    }
    closeModal();
  };

  // Handle auto force end time range change
  const handleAutoTimeRangeChange = (timeRange: number) => {
    setIsAutoMenuOpen(false);

    if (demoBlocksAction) {
      setDemoBlockMessage("Auto Clear is not available in demo mode.");
      return;
    }

    if (timeRange === 0) {
      // No confirmation needed for "None"
      handleAutoTimeRangeUpdate(timeRange);
    } else {
      // Show confirmation modal for time ranges
      setPendingTimeRange(timeRange);
      setModalType("autoTimeRange");
      setModalOpen(true);
    }
  };

  const confirmAutoTimeRangeChange = async () => {
    if (pendingTimeRange !== null) {
      await handleAutoTimeRangeUpdate(pendingTimeRange);
    }
  };

  const handleAutoTimeRangeUpdate = async (timeRange: number) => {
    if (!lotId) {
      setError("Lot ID is missing. Please try again.");
      return;
    }

    try {
      console.log("Updating auto force end time range for lot", lotId, "to", timeRange);
      await lotService.updateAutoForceEndTimeRange(lotId, timeRange);

      // Update local state
      const thresholdMap: { [key: number]: string } = {
        0: "NONE",
        12: "HRS_12",
        24: "HRS_24",
        48: "HRS_48"
      };
      setAutoForceEndThreshold(thresholdMap[timeRange] || "NONE");
    } catch (err: any) {
      console.error("Failed to update auto force end time range:", err);
      setError("Failed to update auto force end setting. Please try again.");
    }
    closeModal();
  };

  // Helper function to get display text for auto force end threshold
  const getAutoDisplayText = (threshold: string) => {
    const displayMap: { [key: string]: string } = {
      "NONE": "OFF",
      "HRS_12": "12HR",
      "HRS_24": "24HR",
      "HRS_48": "48HR"
    };
    return displayMap[threshold] || "None";
  };

  // Helper function to get CSS class for the text
  const getAutoTextClass = (threshold: string) => {
    if (threshold === "NONE") return "";
    return "time-text";
  };

  // Helper function to get modal title and description
  const getAutoTimeRangeModalInfo = (timeRange: number) => {
    switch (timeRange) {
      case 12:
        return {
          title: "Confirm 12 Hour Occupancy Reset",
          description: "Every Mid-day, (12:00pm) and every Mid-Night (12:00am), all occupants who have been parked for over 12 hours will be removed."
        };
      case 24:
        return {
          title: "Confirm 24 Hour Occupancy Reset",
          description: "Every Mid-Night (12:00am), all occupants who have been parked for over 8 hours will be removed."
        };
      case 48:
        return {
          title: "Confirm 48 Hour Occupancy Reset",
          description: "Every other (second) Mid-Night (12:00am), all occupants who have been parked for over 8 hours will be removed."
        };
      default:
        return {
          title: "Confirm Auto Reset",
          description: "This will enable automatic removal of occupants based on the selected time range."
        };
    }
  };

  const autoMenuItems: MenuItem[] = useMemo(() => [
    {
      key: "auto-none",
      label: "None",
      selected: autoForceEndThreshold === "NONE",
      onSelect: () => handleAutoTimeRangeChange(0),
    },
    {
      key: "auto-12",
      label: "12HR",
      selected: autoForceEndThreshold === "HRS_12",
      onSelect: () => handleAutoTimeRangeChange(12),
    },
    {
      key: "auto-24",
      label: "24HR",
      selected: autoForceEndThreshold === "HRS_24",
      onSelect: () => handleAutoTimeRangeChange(24),
    },
    {
      key: "auto-48",
      label: "48HR",
      selected: autoForceEndThreshold === "HRS_48",
      onSelect: () => handleAutoTimeRangeChange(48),
    },
  ], [autoForceEndThreshold, handleAutoTimeRangeChange]);

  // occupant status
  const getStatus = (s: ParkingSession) => {
    if (s.vehicleRegistered) return "Registered";
    if (s.sessionType === "Validated") return "Validated";
    if (s.userType === "Violator") return "Violator";
    return "Active";
  };

  // Local sort state for multi-lot mode (single-lot uses pagination hook)
  const [localSortBy, setLocalSortBy] = useState("duration");
  const [localSortOrder, setLocalSortOrder] = useState<"asc" | "desc">("asc");
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Determine effective sort values based on mode
  const effectiveSortBy = isMultiLotMode ? localSortBy : (sortBy || "entryTime");
  const effectiveSortOrder = isMultiLotMode ? localSortOrder : sortDirection;

  // sorting
  const handleSort = (column: string) => {
    if (isMultiLotMode) {
      // Client-side sorting for multi-lot mode
      setLocalSortOrder(localSortBy === column && localSortOrder === "asc" ? "desc" : "asc");
      setLocalSortBy(column);
    } else {
      // Server-side sorting for single-lot mode
      const apiField = sortColumnToApiField[column] || column;
      if (sortBy === apiField) {
        setSort(apiField, sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSort(apiField, "asc");
      }
    }
  };

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (!isMultiLotMode) {
      setSearch(value); // Server-side search
    }
  };

  // duration
  const getDurationMinutes = (time: string) => {
    const now = new Date();
    const entered = new Date(time);
    const durationMs = now.getTime() - entered.getTime();
    const durationMinutes = Math.floor(durationMs / 60000);

    // Ensure we never show negative duration (minimum 0)
    return Math.max(0, durationMinutes);
  };
  const formatDuration = (m: number) => {
    if (m < 60) return `${m} mins`;

    const hours = Math.floor(m / 60);
    const remainingMins = m % 60;

    if (hours < 24) {
      return `${hours}h ${remainingMins}m`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  // filter & sort - only for multi-lot mode (single-lot uses server pagination)
  const getFilteredSessions = () => {
    if (!isMultiLotMode) {
      // Single-lot mode: server handles filtering/sorting
      return sessions;
    }

    // Multi-lot mode: client-side filtering and sorting
    return sessions
      .filter((s) =>
        Object.values(s).some((val) =>
          val?.toString().toLowerCase().includes(searchInput.toLowerCase())
        )
      )
      .sort((a, b) => {
        let valA: string | number = "";
        let valB: string | number = "";

        if (localSortBy === "plate") {
          valA = a.plateNumber || "";
          valB = b.plateNumber || "";
        } else if (localSortBy === "arrivalDate") {
          valA = new Date(a.entryTime).setHours(0, 0, 0, 0);
          valB = new Date(b.entryTime).setHours(0, 0, 0, 0);
        } else if (localSortBy === "arrivalTime") {
          const dA = new Date(a.entryTime);
          const dB = new Date(b.entryTime);
          valA = dA.getHours() * 60 + dA.getMinutes();
          valB = dB.getHours() * 60 + dB.getMinutes();
        } else if (localSortBy === "duration") {
          const now = new Date();
          valA = now.getTime() - new Date(a.entryTime).getTime();
          valB = now.getTime() - new Date(b.entryTime).getTime();
        } else if (localSortBy === "status") {
          valA = getStatus(a);
          valB = getStatus(b);
        } else if (localSortBy === "lotName") {
          valA = a.lotName || "";
          valB = b.lotName || "";
        }

        if (typeof valA === "number" && typeof valB === "number") {
          return localSortOrder === "asc" ? valA - valB : valB - valA;
        }
        if (typeof valA === "string" && typeof valB === "string") {
          return localSortOrder === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return 0;
      });
  };

  const filteredSessions = getFilteredSessions();

  // Pagination logic - different for single-lot vs multi-lot
  const totalItems = isMultiLotMode ? filteredSessions.length : totalElements;
  const effectiveTotalPages = isMultiLotMode
    ? Math.ceil(filteredSessions.length / itemsPerPage)
    : totalPages;
  const effectiveCurrentPage = isMultiLotMode ? localCurrentPage : page + 1; // Convert 0-indexed to 1-indexed for display

  // Get current items to display
  const currentItems = isMultiLotMode
    ? filteredSessions.slice((localCurrentPage - 1) * itemsPerPage, localCurrentPage * itemsPerPage)
    : sessions; // Server already paginates for single-lot

  // format date/time
  const getDateStr = (t: string) => {
    const d = new Date(t);
    return d.toLocaleDateString();
  };
  const getTimeStr = (t: string) => {
    const d = new Date(t);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const displayOccupancy = isMultiLotMode
    ? multiLotOccupancyFromLots ?? currentOccupancy ?? occupantCount
    : currentOccupancy ?? occupantCount;
  const capacityDisplay = lotCapacity || 0;

  const overrideModal = (
    <Modal
      isOpen={isEditModalOpen}
      title="Override occupancy"
      description="Set the current occupancy without starting or ending sessions."
      confirmText="Save"
      cancelText="Cancel"
      onConfirm={handleSaveOverride}
      onCancel={handleCloseOverrideModal}
      disableConfirm={isSavingOverride}
    >
      <div className="modal-form">
        <label htmlFor="occupancy-override-input">
          New occupancy (0-10000)
          <input
            id="occupancy-override-input"
            type="number"
            inputMode="numeric"
            min={0}
            max={10000}
            step={1}
            value={overrideValue}
            onChange={(e) => handleOverrideInputChange(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
        </label>
        <div className="modal-helper">
          This only updates the occupancy counter and will not start or end sessions.
        </div>
        {overrideError && <div className="modal-error">{overrideError}</div>}
      </div>
    </Modal>
  );

  if (loading) {
    return (
      <div className="occupants-mobile-wrapper">
        <div className="sticky-header">
          <div className="occupants-header">
            <div className="occupants-title-row">
              <h1>
                Occupants{" "}
                <span className="occupant-count">
                  {displayOccupancy}/{capacityDisplay}
                </span>
              </h1>
              {/* Override button - disabled in multi-lot mode */}
              {canOverrideOccupancy && !isMultiLotMode && (
                <button
                  type="button"
                  className="occupants-edit-button"
                  onClick={handleOpenOverrideModal}
                  aria-label="Override occupancy count"
                >
                  <img src="/assets/Edit.svg" alt="" aria-hidden="true" />
                  <span>Edit</span>
                </button>
              )}
            </div>
            <p>Current Vehicle Plates in Your {isMultiLotMode ? "Lots" : "Lot"}</p>
          </div>

        <div className="search-and-actions">
          <div className="search-bar">
            <img src="/assets/SearchBarIcon.svg" alt="Search" />
            <input
              type="text"
              placeholder="Search plate, time, date, duration..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="occupants-actions">
            <button
              className="button-secondary clear-all-button"
              onClick={handleClearAllModal}
            >
              Clear All
            </button>

            {/* Auto dropdown - disabled in multi-lot mode */}
            {!isMultiLotMode && (
              <div className="auto-dropdown-container">
                <div className="menu-wrapper">
                  <Menu
                    trigger={
                      <button
                        type="button"
                        className={`button-secondary auto-button ${autoForceEndThreshold === "NONE" ? 'none-selected' : ''}`}
                      >
                        Auto <span className={getAutoTextClass(autoForceEndThreshold)}>{getAutoDisplayText(autoForceEndThreshold)}</span>
                        <img
                          src="/assets/ArrowDropdown.svg"
                          alt="Dropdown"
                          className={`dropdown-arrow ${isAutoMenuOpen ? 'rotated' : ''}`}
                        />
                      </button>
                    }
                    items={autoMenuItems}
                    placement="bottom-end"
                    onOpenChange={setIsAutoMenuOpen}
                    allowNoSelection={false}
                    usePortal
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

        <div className="table-container occupants-table-skeleton">
          <div className="skeleton-block occupants-table-skeleton-block" aria-hidden="true" />
        </div>
        <div className="occupants-pagination-skeleton">
          <div className="skeleton-block occupants-page-pill" aria-hidden="true" />
          <div className="skeleton-block occupants-page-pill" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (error) {
    return <Error message={error} />;
  }

  return (
    <div className="occupants-mobile-wrapper">
      <div className="sticky-header">
        <div className="occupants-header">
          <div className="occupants-title-row">
            <h1>
              Occupants{" "}
              <span className="occupant-count">
                {displayOccupancy}/{capacityDisplay}
              </span>
            </h1>
            {/* Override button - disabled in multi-lot mode */}
            {canOverrideOccupancy && !isMultiLotMode && (
              <button
                type="button"
                className="occupants-edit-button"
                onClick={handleOpenOverrideModal}
                aria-label="Override occupancy count"
              >
                <img src="/assets/Edit.svg" alt="" aria-hidden="true" />
                <span>Edit</span>
              </button>
            )}
          </div>
          <p>Current Vehicle Plates in Your {isMultiLotMode ? "Lots" : "Lot"}</p>
        </div>

        <div className="search-and-actions">
          <div className="search-bar">
            <img src="/assets/SearchBarIcon.svg" alt="Search" />
            <input
              type="text"
              placeholder="Search plate, time, date, duration..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="occupants-actions">
            <button
              className="button-secondary clear-all-button"
              onClick={handleClearAllModal}
            >
              Clear All
            </button>

            {/* Auto dropdown - disabled in multi-lot mode */}
            {!isMultiLotMode && (
              <div className="auto-dropdown-container">
                <div className="menu-wrapper">
                  <Menu
                    trigger={
                    <button
                      type="button"
                      className={`button-secondary auto-button ${autoForceEndThreshold === "NONE" ? 'none-selected' : ''}`}
                    >
                      Auto <span className={getAutoTextClass(autoForceEndThreshold)}>{getAutoDisplayText(autoForceEndThreshold)}</span>
                      <img
                        src="/assets/ArrowDropdown.svg"
                        alt="Dropdown"
                        className={`dropdown-arrow ${isAutoMenuOpen ? 'rotated' : ''}`}
                      />
                    </button>
                  }
                  items={autoMenuItems}
                  placement="bottom-end"
                  onOpenChange={setIsAutoMenuOpen}
                  allowNoSelection={false}
                  usePortal
                />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="table-container" ref={tableContainerRef}>
        <div className="mobile-table-wrapper">
          <table className={`occupants-table app-table app-table-secondary-hover ${totalItems === 0 ? 'empty' : ''} ${isCondensed ? 'condensed' : ''}`}>
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("plate")}
                  className={`sortable-column ${sortBy === "plate" ? "active" : ""}`}
                >
                  Plate
                  <img
                    src={`/assets/tables/${sortBy === "plate" ? "SortArrowSelected.svg" : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${effectiveSortOrder}`}
                  />
                </th>
                <th
                  onClick={() => handleSort("arrivalTime")}
                  className={`sortable-column ${sortBy === "arrivalTime" ? "active" : ""
                    }`}
                >
                  Arrival Time
                  <img
                    src={`/assets/tables/${sortBy === "arrivalTime"
                      ? "SortArrowSelected.svg"
                      : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${effectiveSortOrder}`}
                  />
                </th>
                <th
                  onClick={() => handleSort("arrivalDate")}
                  className={`sortable-column ${sortBy === "arrivalDate" ? "active" : ""
                    }`}
                >
                  Arrival Date
                  <img
                    src={`/assets/tables/${sortBy === "arrivalDate"
                      ? "SortArrowSelected.svg"
                      : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${effectiveSortOrder}`}
                  />
                </th>
                <th
                  onClick={() => handleSort("duration")}
                  className={`sortable-column ${sortBy === "duration" ? "active" : ""
                    }`}
                >
                  Duration
                  <img
                    src={`/assets/tables/${sortBy === "duration"
                      ? "SortArrowSelected.svg"
                      : "SortArrow.svg"
                      }`}
                    alt="Sort"
                    className={`sort-arrow ${effectiveSortOrder}`}
                  />
                </th>
                {/* Lot Name column - only shown in multi-lot mode */}
                {isMultiLotMode && !isCondensed && (
                  <th
                    onClick={() => handleSort("lotName")}
                    className={`sortable-column ${sortBy === "lotName" ? "active" : ""}`}
                  >
                    Lot Name
                    <img
                      src={`/assets/tables/${sortBy === "lotName"
                        ? "SortArrowSelected.svg"
                        : "SortArrow.svg"
                        }`}
                      alt="Sort"
                      className={`sort-arrow ${effectiveSortOrder}`}
                    />
                  </th>
                )}
                {!isCondensed && (
                  <th
                    onClick={() => handleSort("status")}
                    className={`sortable-column ${sortBy === "status" ? "active" : ""}`}
                  >
                    Status
                    <img
                      src={`/assets/tables/${sortBy === "status"
                        ? "SortArrowSelected.svg"
                        : "SortArrow.svg"
                        }`}
                      alt="Sort"
                      className={`sort-arrow ${effectiveSortOrder}`}
                    />
                  </th>
                )}
                {/* Narrow column for remove icon (no heading) - hidden in condensed mode */}
                {!isCondensed && <th className="remove-column"></th>}
                {/* Actions column for condensed mode */}
                {isCondensed && <th className="actions-column"></th>}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((session) => {
                // Log each session so we can debug in the browser console
                console.log("Session in list:", session);

                const durationMins = getDurationMinutes(session.entryTime);
                const isVal = session.sessionType === "Validated";
                const isReg = session.vehicleRegistered;
                const statusStr = getStatus(session);

                // Make sure we have a valid session ID
                if (!session.sessionId) {
                  console.error("Session missing ID:", session);
                }

                return (
                  <tr key={session.sessionId || Math.random()}>
                    <td>
                      <div className="plate-badge">{session.plateNumber}</div>
                    </td>
                    <td>{getTimeStr(session.entryTime)}</td>
                    <td>{getDateStr(session.entryTime)}</td>
                    <td>{formatDuration(durationMins)}</td>
                    {/* Lot Name column - only shown in multi-lot mode */}
                    {isMultiLotMode && !isCondensed && (
                      <td>{session.lotName}</td>
                    )}
                    {/* Status column - hidden in condensed mode */}
                    {!isCondensed && (
                      <td>
                        {/* Desktop validation logic */}
                        {/* First priority: Show "Registered" if in registry */}
                        {isReg && (
                          <span className="registered-label">Registered</span>
                        )}
                        {/* Show validation button for violators if validation is allowed */}
                        {!isReg && session.userType === "Violator" && allowValidation && !isVal && (
                          <button
                            className="validate-button violator-validate"
                            onClick={() => handleValidateViolatorModal(session.sessionId)}
                          >
                            <img src="/assets/occupants/validate.svg" alt="Validate" className="validate-icon" />
                            <img src="/assets/occupants/unvalidate.svg" alt="Remove Validation" className="unvalidate-icon" />
                            Validate
                          </button>
                        )}
                        {/* Show "Violator" if userType is Violator and validation not allowed */}
                        {!isReg && session.userType === "Violator" && !allowValidation && (
                          <span className="violator-label">Violator</span>
                        )}
                        {/* If not registered but validation is allowed and not validated, show validate button */}
                        {!isReg && session.userType !== "Violator" && allowValidation && !isVal && (
                          <button
                            className="validate-button"
                            onClick={() => handleValidate(session.sessionId)}
                          >
                            <img src="/assets/occupants/validate.svg" alt="Validate" className="validate-icon" />
                            <img src="/assets/occupants/unvalidate.svg" alt="Remove Validation" className="unvalidate-icon" />
                            Validate
                          </button>
                        )}
                        {/* If not registered but validated, show validated button */}
                        {!isReg && session.userType !== "Violator" && allowValidation && isVal && (
                          <button
                            className="validate-button validated"
                            onClick={() => handleRemoveValidationModal(session.sessionId)}
                          >
                            <img src="/assets/occupants/validate.svg" alt="Validate" className="validate-icon" />
                            <img src="/assets/occupants/unvalidate.svg" alt="Remove Validation" className="unvalidate-icon" />
                            <span className="validated-text">Validated</span>
                            <span className="unvalidate-text">Remove</span>
                          </button>
                        )}
                        {/* If validation isn't allowed and status is Active, we show blank or any label */}
                        {!isReg && session.userType !== "Violator" && !allowValidation && statusStr === "Active" && <span></span>}
                      </td>
                    )}
                    {/* Remove column - hidden in condensed mode */}
                    {!isCondensed && (
                      <td>
                        <div className="occupants-actions-container">
                          <Tooltip text="Remove" position="left" size="small" delayMs={600}>
                            <img
                              src="/assets/Minus.svg"
                              alt="Remove occupant"
                              className="remove-icon"
                              onClick={() => {
                                console.log(`Initiating force end of session: ${session.sessionId}`);
                                handleRemoveVehicleModal(session.sessionId);
                              }}
                            />
                          </Tooltip>
                        </div>
                      </td>
                    )}
                    {/* Condensed mode actions menu */}
                    {isCondensed && (
                      <td className="condensed-actions-cell">
                        <Menu
                          trigger={
                            <button className="condensed-actions-trigger" aria-label="Actions">
                              <img src="/assets/occupants/mobile_more.svg" alt="Actions" />
                            </button>
                          }
                          placement="bottom-end"
                          matchTriggerWidth={false}
                          items={(() => {
                            const menuItems: MenuItem[] = [];

                            // Add validate option if applicable
                            if (!isReg && allowValidation) {
                              if (session.userType === "Violator" && !isVal) {
                                menuItems.push({
                                  key: "validate",
                                  label: "Validate",
                                  onSelect: () => handleValidateViolatorModal(session.sessionId),
                                });
                              } else if (session.userType !== "Violator" && !isVal) {
                                menuItems.push({
                                  key: "validate",
                                  label: "Validate",
                                  onSelect: () => handleValidate(session.sessionId),
                                });
                              } else if (session.userType !== "Violator" && isVal) {
                                menuItems.push({
                                  key: "remove-validation",
                                  label: "Remove Validation",
                                  onSelect: () => handleRemoveValidationModal(session.sessionId),
                                });
                              }
                            }

                            // Always add remove option
                            menuItems.push({
                              key: "remove",
                              label: "Remove",
                              tone: "danger",
                              onSelect: () => handleRemoveVehicleModal(session.sessionId),
                            });

                            return menuItems;
                          })()}
                        />
                      </td>
                    )}
                    {/* Mobile-only columns */}
                    <td className="mobile-only">
                      {formatDuration(durationMins)}
                    </td>
                    <td className="mobile-only">
                      {isReg && (
                        <img src="/assets/occupants/mobile_registered.svg" alt="Registered" className="mobile-status-icon" />
                      )}
                      {!isReg && session.userType === "Violator" && (
                        <img src="/assets/occupants/mobile_violator.svg" alt="Violator" className="mobile-status-icon" />
                      )}
                    </td>
                    <td className="mobile-only">
                      <img
                        src="/assets/occupants/mobile_more.svg"
                        alt="More options"
                        className="mobile-more-icon"
                        onClick={(e) => handleMobileMenuOpen(session, e)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table Footer */}
      <Pagination
        currentPage={isMultiLotMode ? localCurrentPage - 1 : page}
        totalPages={effectiveTotalPages}
        totalItems={totalItems}
        itemsOnPage={currentItems.length}
        onPageChange={(newPage) => {
          if (isMultiLotMode) {
            setLocalCurrentPage(newPage + 1);
          } else {
            goToPage(newPage);
          }
        }}
        disabled={loading}
        emptyMessage={isMultiLotMode
          ? "No vehicles are currently parked in your lots."
          : "No vehicles are currently parked in your lot."}
      />

      {/* MODALS */}
      {modalOpen && modalType === "removeVehicle" && (
        <Modal
          isOpen
          title="Remove Vehicle"
          description="Confirming this action will remove this vehicle from your lot. They will not be billed or logged. You cannot undo this action."
          confirmText="Remove Vehicle"
          cancelText="Cancel"
          onConfirm={confirmRemoveVehicle}
          onCancel={closeModal}
        />
      )}

      {modalOpen && modalType === "removeValidation" && (
        <Modal
          isOpen
          title="Remove Validation"
          description="This vehicle was already validated. Removing validation means the vehicle will be billed for their stay."
          confirmText="Remove Validation"
          cancelText="Keep Validated"
          onConfirm={confirmRemoveValidation}
          onCancel={closeModal}
        />
      )}

      {demoBlockMessage && (
        <Modal
          isOpen={true}
          title={demoBlockMessage}
          description="This action is disabled while the lot is in demo mode."
          confirmText=""
          cancelText="OK"
          onConfirm={() => { }}
          onCancel={() => setDemoBlockMessage(null)}
        />
      )}

      {modalOpen && modalType === "clearAll" && (
        <Modal
          isOpen
          title="Clear All Vehicles"
          description={isMultiLotMode
            ? "This will remove all vehicles from all selected lots. They will not be billed or logged. You cannot undo this action."
            : "This will remove all vehicles from your lot. They will not be billed or logged. You cannot undo this action."}
          confirmText="Clear All"
          cancelText="Cancel"
          onConfirm={confirmClearAll}
          onCancel={closeModal}
        />
      )}

      {modalOpen && modalType === "autoTimeRange" && pendingTimeRange !== null && (
        <Modal
          isOpen
          title={getAutoTimeRangeModalInfo(pendingTimeRange).title}
          description={getAutoTimeRangeModalInfo(pendingTimeRange).description}
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={confirmAutoTimeRangeChange}
          onCancel={closeModal}
        />
      )}

      {modalOpen && modalType === "validateViolator" && (
        <Modal
          isOpen
          title="Validate Violator"
          description="If you proceed with validating this vehicle, their current violation will be removed. This action cannot be undone."
          confirmText="Validate Vehicle"
          cancelText="Cancel"
          onConfirm={confirmValidateViolator}
          onCancel={closeModal}
        />
      )}

      {overrideModal}

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        onValidate={handleMobileValidate}
        onRemove={handleMobileRemove}
        canValidate={allowValidation}
        isRegistered={mobileMenuSession?.vehicleRegistered || false}
        isViolator={mobileMenuSession?.userType === "Violator" || false}
        isValidated={mobileMenuSession?.sessionType === "Validated" || false}
        position={mobileMenuPosition}
      />

    </div>
  );
};

export default Occupants;
